import React, { useMemo, useState } from "react";
import { Box, Link, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../../common/CommonLoader/CommonLoader";
import AppButton from "../../../common/AppButton";
import ArchiveEntityDialog from "../../common/ArchiveEntityDialog";
import { formatEntityStatus, isArchived } from "../common/academicUtils";
import {
  useAcademicPrograms,
  useAllGrades,
  useAllStreams,
  useArchiveGrade
} from "../../../../hooks/api/education/useAcademicStructure";

const GradeDetail: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { gradeId = "" } = useParams();
  const { data: programs = [] } = useAcademicPrograms();
  const { data: grades = [], isPending, refetch } = useAllGrades();
  const { data: streams = [] } = useAllStreams(gradeId);
  const archiveGrade = useArchiveGrade();
  const [archiveOpen, setArchiveOpen] = useState(false);

  const grade = useMemo(
    () => grades.find((item) => item.id === gradeId),
    [grades, gradeId]
  );

  const program = useMemo(
    () => programs.find((item) => item.id === grade?.programId),
    [programs, grade]
  );

  if (isPending) {
    return (
      <CommonLoader loadingMessage={t("loading-grade", "Loading grade...")} />
    );
  }

  if (!grade) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>{t("grade-not-found", "Grade not found.")}</Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="grade-detail-page" sx={{ p: 3 }}>
      <Link component={RouterLink} to="..">
        {t("back-to-grades", "Back to grades")}
      </Link>

      <Typography variant="h5" component="h1" sx={{ mt: 2, mb: 0.5 }}>
        {grade.name}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {formatEntityStatus(grade.status)}
      </Typography>

      <Typography variant="body2" sx={{ mb: 2 }}>
        {t("program-label", "Program")}:{" "}
        {program ? (
          <Link component={RouterLink} to={`../programs/${program.id}`}>
            {program.name}
          </Link>
        ) : (
          grade.programId
        )}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        {!isArchived(grade.status) ? (
          <AppButton variant="text" onClick={() => setArchiveOpen(true)}>
            {t("archive", "Archive")}
          </AppButton>
        ) : null}
        <AppButton
          variant="secondary"
          onClick={() => navigate(`../streams?gradeId=${grade.id}`)}
        >
          {t("view-streams", "View streams")}
        </AppButton>
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        {t("streams-in-grade", "Streams in this grade")}
      </Typography>
      {streams.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          {t("streams-empty", "No streams yet for this grade.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {streams.map((stream) => (
            <Box
              component="li"
              key={stream.id}
              sx={{
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                justifyContent: "space-between"
              }}
            >
              <Typography variant="body2">{stream.name}</Typography>
              <AppButton
                variant="text"
                size="small"
                onClick={() => navigate(`../streams/${stream.id}`)}
              >
                {t("view", "View")}
              </AppButton>
            </Box>
          ))}
        </Box>
      )}

      <ArchiveEntityDialog
        open={archiveOpen}
        entityLabel={grade.name}
        loading={archiveGrade.isPending}
        onClose={() => setArchiveOpen(false)}
        onConfirm={async (reason) => {
          await archiveGrade.mutateAsync({
            gradeId: grade.id,
            expectedVersion: grade.version ?? undefined,
            reason: reason || undefined
          });
          setArchiveOpen(false);
          refetch();
        }}
      />
    </Box>
  );
};

export default GradeDetail;
